import React, { Fragment } from 'react';
import moment from 'moment';
import {
  CardBody,
  Card,
  Label,
  Input,
  Row,
  Col,
  FormGroup,
  Button,
  Form,
  Alert,
} from 'reactstrap';
import CSSTransitionGroup from 'react-transition-group/CSSTransitionGroup';
import PageTitle from 'Layout/Components/PageTitle';
import ReactTable from "react-table";
import { toast } from 'react-toastify';
import { withTranslation } from 'react-i18next';
import { listAbuse, searchAbuse, classifyAbuse, updateAbuse, deleteAbuse } from 'API/AbuseAPI';
import ConfirmAction from 'Layout/Components/ConfirmAction';
import ReportedUrlModal from './ReportedUrlModal';

const TYPE = {
    PHISHING: "phishing",
    LEGITIMATE: "legitimate"
}

const STATUS = {
    ACCEPTED: "accepted",
    NOT_ACCEPTED: "not_accepted",
    NULL: "null"
}

function isValidUrl(input) {
  try {
    const url = new URL(input.includes('://') ? input : `http://${input}`);
    return Boolean(input && url.hostname && ['http:', 'https:'].includes(url.protocol));
  } catch (error) {
    return false;
  }
}

export class ReportedUrls extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
        type: TYPE.PHISHING,
        status: STATUS.ACCEPTED,
        reported_urls: [],
        totalPages: 1,
        pageSize: 10,
        page: 0,
        openModal: false,
        id: "",
        urlInput: '',
        activeUrl: null,
        lookupResult: null,
        lookupError: null,
        loading: false,
        classificationUrl: '',
        classificationType: '',
        classificationSaving: false,
        classificationResult: null,
        classificationError: null
      };
  
      this.fillTable = this.fillTable.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.searchUrls = this.searchUrls.bind(this);
      this.updateUrl = this.updateUrl.bind(this); 
      this.deleteUrl = this.deleteUrl.bind(this);
      this.lookupUrl = this.lookupUrl.bind(this);
      this.clearLookup = this.clearLookup.bind(this);
      this.classifyUrl = this.classifyUrl.bind(this);
      this.requestId = 0;
      this.classificationPending = false;
      this.mounted = false;
  }

  componentDidMount() {
    this.mounted = true;
    this.fillTable(0);
  }
  
  componentWillUnmount() {
    this.mounted = false;
    this.requestId += 1;
  }

  async fillTable(page = 0, activeUrl = this.state.activeUrl) {
    const requestId = ++this.requestId;
    this.setState({ loading: true, activeUrl, lookupResult: null, lookupError: null });
    try {
        const { type, status, pageSize } = this.state; 
        const result = activeUrl
          ? await searchAbuse(activeUrl, page, pageSize)
          : await listAbuse(type, status, page, pageSize);
        if (requestId !== this.requestId) return;
        if (!result || !Array.isArray(result.data)) throw new Error('Invalid reports response');
        this.setState({
          reported_urls: result.data,
          totalPages: Math.max(1, result.totalPages || 0),
          page,
          lookupResult: activeUrl ? { url: activeUrl, count: result.totalElements } : null
        });
    } catch (error) {
      if (requestId !== this.requestId) return;
      this.setState({ reported_urls: [], totalPages: 1, page: 0,
        lookupError: this.props.t('reported_urls.lookup.error') });
      console.error("Error listing Abuse", error);
      toast["error"](this.props.t('errors.loading', { element: 'Abuse', message: error.message }));
    } finally {
      if (requestId === this.requestId) this.setState({ loading: false });
    }
  }

  handleChange(event) {
    const obj = event.target || event;
    const change = { [obj.name]: obj.value };
    if (obj.name === 'classificationUrl' || obj.name === 'classificationType') {
      change.classificationResult = null;
      change.classificationError = null;
    }
    this.setState(change);
  }

  searchUrls(event) {
    if (event) event.preventDefault();
    this.fillTable(0, null);
  }

  lookupUrl(event) {
    event.preventDefault();
    const input = this.state.urlInput.trim();
    if (!isValidUrl(input)) {
      this.setState({ lookupError: this.props.t('reported_urls.lookup.invalid_url') });
      return;
    }
    this.fillTable(0, input);
  }

  clearLookup() {
    this.setState({ urlInput: '' });
    this.fillTable(0, null);
  }

  async classifyUrl(event) {
    event.preventDefault();
    if (this.classificationPending) return;
    const url = this.state.classificationUrl.trim();
    const type = this.state.classificationType;
    if (!isValidUrl(url) || !Object.values(TYPE).includes(type)) {
      this.setState({ classificationError: this.props.t('reported_urls.classify.invalid'), classificationResult: null });
      return;
    }
    this.classificationPending = true;
    this.requestId += 1;
    this.setState({
      classificationSaving: true, loading: false, classificationError: null, classificationResult: null,
      reported_urls: [], totalPages: 1, page: 0, lookupResult: null, lookupError: null
    });
    try {
      await classifyAbuse(url, type);
      if (!this.mounted) return;
      this.setState({
        classificationResult: { url, type },
        urlInput: url,
        type,
        status: STATUS.ACCEPTED
      });
      await this.fillTable(0, url);
    } catch (error) {
      if (!this.mounted) return;
      this.setState({ classificationError: this.props.t('reported_urls.classify.error') });
    } finally {
      this.classificationPending = false;
      if (this.mounted) this.setState({ classificationSaving: false });
    }
  }

  updateUrl(hash, status){  
    updateAbuse(hash, status).then((result) => {
      toast["success"](this.props.t('reported_urls.actions.updated'));
      this.fillTable();
    }).catch((error) => {
      console.error("Error updating URL", error);
      toast["error"](this.props.t('errors.loading', { element: 'URL', message: error.message }));
    });
  }

  deleteUrl(id) {
    deleteAbuse(id).then((result) => {
      toast["success"](this.props.t('reported_urls.actions.deleted'));
      this.fillTable();
    }).catch((error) => {
      console.error("Error deleting URL", error);
      toast["error"](this.props.t('errors.loading', { element: 'URL', message: error.message }));
    });
  }

  render() {
    const { t } = this.props;
    const { lookupResult, lookupError, loading, classificationSaving, classificationResult, classificationError } = this.state;
    const busy = loading || classificationSaving;

    const recordModal = this.state.openModal &&
          <ReportedUrlModal open={true} id={this.state.id}
            onClose={() => this.setState({ openModal: false })} />;

    return (
      <Fragment>
        <PageTitle
          heading={t("reported_urls.title")}
          icon="pe-7s-attention icon-gradient bg-primary"
        />
        
        <CSSTransitionGroup
          component="div"
          transitionName="TabsAnimation"
          transitionAppear={true}
          transitionAppearTimeout={0}
          transitionEnter={false}
          transitionLeave={false}>
          <Row>
            <Col md="12">
              {recordModal}
              <Card className="mb-3">
                <CardBody>
                  <Form onSubmit={this.lookupUrl}>
                    <FormGroup>
                      <Label for="reported-url-search">{t('reported_urls.lookup.title')}</Label>
                      <p id="reported-url-search-help" className="text-muted">
                        {t('reported_urls.lookup.help')}
                      </p>
                      <Input type="text" name="urlInput" id="reported-url-search"
                        placeholder="https://example.com/path" required
                        aria-describedby="reported-url-search-help"
                        value={this.state.urlInput} onChange={this.handleChange} />
                    </FormGroup>
                    <Button type="submit" color="primary" disabled={busy}>
                      {t(loading ? 'reported_urls.lookup.loading' : 'reported_urls.lookup.check')}
                    </Button>{' '}
                    <Button type="button" color="secondary" onClick={this.clearLookup} disabled={busy}>
                      {t('reported_urls.lookup.clear')}
                    </Button>
                  </Form>
                  <div aria-live="polite">
                    {lookupError && <Alert color="danger" className="mt-3 mb-0">{lookupError}</Alert>}
                    {lookupResult && !lookupError && (
                      <Alert color={lookupResult.count > 0 ? 'warning' : 'info'} className="mt-3 mb-0">
                        <div>{t(lookupResult.count > 0 ? 'reported_urls.lookup.found' : 'reported_urls.lookup.not_found')}</div>
                        <div className="text-break">{lookupResult.url}</div>
                      </Alert>
                    )}
                  </div>
                </CardBody>
              </Card>
              <Card className="mb-3">
                <CardBody>
                  <Form id="reported-url-classification-form" onSubmit={this.classifyUrl}>
                    <h5>{t('reported_urls.classify.title')}</h5>
                    <Row>
                      <Col md={8}>
                        <FormGroup>
                          <Label for="reported-url-classification">{t('reported_urls.url')}</Label>
                          <Input type="text" name="classificationUrl" id="reported-url-classification"
                            placeholder="https://example.com/path" required disabled={classificationSaving}
                            value={this.state.classificationUrl} onChange={this.handleChange} />
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label for="reported-url-classification-type">{t('reported_urls.type')}</Label>
                          <Input type="select" name="classificationType" id="reported-url-classification-type"
                            required disabled={classificationSaving} value={this.state.classificationType} onChange={this.handleChange}>
                            <option value="">{t('reported_urls.classify.choose')}</option>
                            {Object.values(TYPE).map(type => <option key={type} value={type}>{type}</option>)}
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>
                    <p className="text-muted">{t(this.state.classificationType === TYPE.LEGITIMATE
                      ? 'reported_urls.classify.legitimate_help' : 'reported_urls.classify.phishing_help')}</p>
                    <Button type="submit" color="primary" disabled={busy}>
                      {t(classificationSaving ? 'reported_urls.classify.saving' : 'reported_urls.classify.save')}
                    </Button>
                  </Form>
                  <div aria-live="polite">
                    {classificationError && <Alert color="danger" className="mt-3 mb-0">{classificationError}</Alert>}
                    {classificationResult && (
                      <Alert color="success" className="mt-3 mb-0">
                        <div>{t(`reported_urls.classify.${classificationResult.type}_success`)}</div>
                        <div className="text-break">{classificationResult.url}</div>
                      </Alert>
                    )}
                  </div>
                </CardBody>
              </Card>
              <Card className="">
                <CardBody>
                  <Form onSubmit={this.searchUrls}>
                  <Row>
                    <Col md={3}>
                      <FormGroup>
                        <Label for="type">{t('reported_urls.type')}</Label>
                        <Input type="select" name="type" id="type"
                          value={this.state.type} onChange={this.handleChange}>
                          {Object.keys(TYPE).map(key => (
                            <option key={key} value={TYPE[key]}>{TYPE[key]}</option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label for="status">{t('reported_urls.status')}</Label>
                        <Input type="select" name="status" id="status"
                          value={this.state.status} onChange={this.handleChange}>
                          {Object.keys(STATUS).map(key => (
                            <option key={key} value={STATUS[key]}>{STATUS[key]}</option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={2}>
                      <FormGroup className='custom-form-button'>
                        <Button type="submit" color="primary" disabled={busy}>{t('reported_urls.actions.search')}</Button>
                      </FormGroup>
                    </Col>
                  </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col md="12">
              <Card className="main-card mb-3">
                <CardBody>
                  <ReactTable
                    columns={[
                      {
                        Header: t("reported_urls.table.url"),
                        accessor: 'url',
                        id: 'url'
                      },
                      {
                        Header: t("reported_urls.table.submission"),
                        accessor: d => {
                          const date = moment(d.submission);
                          if (date.isValid()) {
                            return moment(d.submission)
                              .local()
                              .format("DD-MM-YYYY hh:mm:ss a")
                          }
                          return ''
                        },
                        id: "submission"
                      },
                      {
                        Header: t("reported_urls.table.type"),
                        accessor: 'type'
                      },
                      {
                        Header: t("reported_urls.table.status"),
                        accessor: 'status'
                      },
                      {
                        Header: t("reported_urls.actions.title"),
                        accessor: 'id',
                        Cell: ({ column, cell, row }) => (
                          <div className="d-block w-100 text-center">
                            <Button className="mb-2 mr-2" color="primary" disabled={classificationSaving}
                              onClick={() => this.updateUrl(row._original.sha256, row.status != STATUS.ACCEPTED ? STATUS.ACCEPTED : STATUS.NOT_ACCEPTED)}>
                              {t('reported_urls.actions.update')}
                            </Button>
                            <ConfirmAction btnClassName="mb-2 mr-2" color="danger" disabled={classificationSaving} title={t('actions.confirmation')}
                              message={t('messages.confirm_delete', { element: 'reported url', object: row.url })}
                              onConfirm={() => this.deleteUrl(row.id)}>
                              {t("reported_urls.actions.delete")}
                            </ConfirmAction>
                          </div>
                        )
                      }
                
                    ]}
                    data={this.state.reported_urls}
                    loading={busy}
                    pages={this.state.totalPages}
                    pageSize={this.state.pageSize}
                    page={this.state.page}
                    manual={true}
                    showPageSizeOptions={false}
                    defaultPageSize={10}
                    onPageChange={page => this.fillTable(page)}
                    className="-striped -highlight"
                    getTrProps={(state, rowInfo) => {
                      if (rowInfo && rowInfo.row) {
                        return {
                          onClick: (e) => {
                            if (e.target.tagName.toLowerCase() === 'button') {
                              return; // Ignore clicks on buttons
                            }
                            this.setState({
                              openModal: true,
                              id: rowInfo.original.id
                            })
                          },
                          style: {
                            cursor: 'pointer'
                          }
                        }
                      }
                      return {}
                    }} />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </CSSTransitionGroup>
      </Fragment>)
  }
}

export default withTranslation()(ReportedUrls);
